import React, { useState } from "react";

import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { GET_DEVICES_BY_PROJECT } from "gql/device.operations";
import { MdmDeviceDto, MdmObjectDeviceRelationDto, MdmObjectDto, useCreateDeviceMutation, useGetProjectQuery } from "gql/generated/types";
import { GET_OBJECT } from "gql/object.operations";
import { Dialog } from "primereact/dialog";
import { Link, useParams } from "react-router-dom";
import { JodaTimeUtils } from "utils/joda-time-utils";
import { TableColumn } from "../utils/types";
import { handleDeviceStatus } from "../utils/utils";
import DeviceForm from "./DeviceForm";
import ItemsTable from "./ItemsTable";

type ObjectDevicesProps = {
  object: MdmObjectDto
}

const ObjectDevices: React.FC<ObjectDevicesProps> = ({object}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { projectId } = useParams()
  const { data: projectData } = useGetProjectQuery({
    variables: { id: projectId! },
    skip: !projectId,
  });
  const [createDeviceMutation] = useCreateDeviceMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Device created successfully",
        life: 3000,
      });
      setDialogOpen(false);
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
  });

  const handleSubmit = (device: Partial<MdmDeviceDto>) => {
    const { objectRelation, project, ...restDevice } = device
    createDeviceMutation({
      variables: {
        device: { ...restDevice, roleInObject: objectRelation?.roleInObject },
        objectId: object.id!,
      },
      update: (cache, { data }) => {
        cache.updateQuery(
          { query: GET_DEVICES_BY_PROJECT, variables: { id: projectId } },
          oldData =>
            oldData
              ? { mdmDevicesByProjectId: [...oldData.mdmDevicesByProjectId, data?.createMdmDevice] }
              : null
        );
        cache.updateQuery({ query: GET_OBJECT, variables: { id: object.id } }, oldData =>
          oldData
            ? {
                mdmObjectById: {
                  ...oldData?.mdmObjectById,
                  deviceRelations: [
                    ...(oldData?.mdmObjectById.deviceRelations ?? []),
                    { mdmDevice: data?.createMdmDevice },
                  ],
                },
              }
            : null
        );
      },
    });
  }

  const renderActions = (rowData: MdmObjectDeviceRelationDto) => (
    <Link to={`/mdm/${projectId}/devices/${rowData.mdmDevice!.id}`} title="Device Info">
        <i className="pi pi-external-link" />
      </Link>
  )

  const columns: TableColumn<MdmObjectDeviceRelationDto>[] = [
    { field: "mdmDevice.identifier", header: "identifier", columnKey: "identifier" },
    { field: "roleInObject", header: "Type",
      body: rowData =>
        projectData?.mdmProjectById.configuration?.deviceConfiguration.measurementTypes?.find(
          type => type?.id === rowData.roleInObject
        )?.title ?? "",
      sortable: true,
      columnKey: "roleInObject",
     },
    {
      field: "mdmDevice.status.status",
      header: "Status",
      body: data => handleDeviceStatus(data.mdmDevice),
      columnKey: "status",
    },
    {
      field: "mdmDevice.createdAt",
      header: "Created",
      body: rowData => JodaTimeUtils.formatInstantAsLocal(rowData.mdmDevice?.modifiedAt),
      columnKey: "createdAt",
    },
    {
      field: "mdmDevice.modifiedAt",
      header: "Updated",
      body: rowData => JodaTimeUtils.formatInstantAsLocal(rowData.mdmDevice?.modifiedAt),
      columnKey: "modifiedAt",
    },
    {
      header: "Actions",
      body: renderActions,
      hideInFile: true,
      columnKey: "actions",
    },
  ];

  return (
    <div>
      <ItemsTable
        columns={columns}
        tableName="DevicesByObject"
        data={object.deviceRelations ?? []}
        onToolbarBtnClick={() => setDialogOpen(true)}
        searchFields={["mdmDevice.identifier", "mdmDevice.tags", "roleInObject"]}
      />
      <Dialog
        onHide={() => setDialogOpen(false)}
        header="Add device"
        visible={dialogOpen}
        style={{ width: "min(100vw, 800px)" }}
      >
        <DeviceForm
          onSubmit={handleSubmit}
          device={{ identifier: "", objectRelation: { mdmObject: object }, tags: [] }}
          newForm
        />
      </Dialog>
    </div>
  );
}

export default ObjectDevices;